import React, { useEffect, useState } from 'react'
import { toast } from '../../utils/toast'
import { XIcon, CheckIcon } from '@phosphor-icons/react'
import ModalContainer from '../ModalContainer/ModalContainer'
import {
  useCreateClientMutation,
  useUpdateClientMutation,
} from '../../redux/client.endpoints'
import { Client } from '../../types/Client'

export type ClientModalMode = 'add' | 'edit'

interface Props {
  isOpen: boolean
  mode: ClientModalMode
  client?: Client
  companyId: number
  onClose: () => void
  showActiveToggle?: boolean
}

const ClientModal = ({
  isOpen,
  mode,
  client,
  companyId,
  onClose,
  showActiveToggle = true,
}: Props) => {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [isActive, setIsActive] = useState(true)
  const [createClient, createClientResult] = useCreateClientMutation()
  const [updateClient, updateClientResult] = useUpdateClientMutation()

  useEffect(() => {
    if (!isOpen) return

    if (mode === 'edit' && client) {
      setName(client.name)
      setDescription(client.description || '')
      setIsActive(client.isActive)
      return
    }

    setName('')
    setDescription('')
    setIsActive(true)
  }, [isOpen, mode, client])

  useEffect(() => {
    if (createClientResult.isSuccess) {
      toast.success('Client created successfully!', {
        toastId: 'create-client-success',
      })
      onClose()
    } else if (createClientResult.isError) {
      toast.error('An error occurred when creating the client.', {
        toastId: 'create-client-error',
      })
    }
  }, [createClientResult])

  useEffect(() => {
    if (updateClientResult.isSuccess) {
      toast.success('Client updated successfully!', {
        toastId: 'update-client-success',
      })
      onClose()
    } else if (updateClientResult.isError) {
      toast.error('An error occurred when updating the client.', {
        toastId: 'update-client-error',
      })
    }
  }, [updateClientResult])

  const handleSubmit = () => {
    console.log('name', name)
    if (!name.trim()) {
      toast.error('Client name is required.', {
        toastId: 'client-name-required',
      })
      return
    }

    if (mode === 'edit' && client) {
      updateClient({
        id: client.id,
        name: name.trim(),
        description: description.trim(),
        isActive,
        companyId,
      })
      return
    }

    createClient({
      name: name.trim(),
      description: description.trim(),
      isActive,
      companyId,
    })
  }

  const isSubmitting =
    createClientResult.isLoading || updateClientResult.isLoading

  return (
    <ModalContainer
      isModalOpen={isOpen}
      hideModal={onClose}
      className="management-clients__modal"
      overlayClassName="management-clients__modal-overlay"
    >
      <div>
        <div className="management-clients__modal-header">
          <h2 className="management-clients__modal-title">
            {mode === 'edit' ? 'Edit Client' : 'Add New Client'}
          </h2>
          <button
            type="button"
            className="management-clients__modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            <XIcon size={20} weight="regular" />
          </button>
        </div>
        <div className="management-clients__modal-body">
          <div className="management-clients__field">
            <label className="management-clients__field-label">
              Name <span>*</span>
            </label>
            <input
              className="management-clients__input"
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              maxLength={25}
              placeholder="Client name"
            />
          </div>
          <div className="management-clients__field">
            <label className="management-clients__field-label">
              Description
            </label>
            <textarea
              className="management-clients__textarea"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Brief description"
              maxLength={500}
            />
          </div>
          {showActiveToggle && (
            <label className="management-clients__checkbox">
              <span
                className={`management-clients__checkbox-box ${
                  isActive ? 'management-clients__checkbox-box--checked' : ''
                }`}
                onClick={() => setIsActive(prev => !prev)}
              >
                {isActive && (
                  <CheckIcon size={12} weight="bold" color="#fff" />
                )}
              </span>
              <span onClick={() => setIsActive(prev => !prev)}>
                Set client as active
              </span>
            </label>
          )}
          <div className="management-clients__modal-actions">
            <button
              type="button"
              className="management-clients__btn management-clients__btn--secondary"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="management-clients__btn management-clients__btn--primary"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {mode === 'edit' ? 'Update' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </ModalContainer>
  )
}

export default ClientModal
